"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import type { Route } from "next";
import { ArrowLeft, GraduationCap, MapPin, Search, ShieldCheck, UsersRound } from "lucide-react";

type Institution = {
  id?: number;
  name?: string;
  location?: string;
  city?: string;
  category?: string;
  description?: string;
  status?: string;
  created_at?: string;
};

type TrainingInstitutionsPayload = {
  success?: boolean;
  data?: Institution[];
};

async function fetchTrainingInstitutions(): Promise<{ rows: Institution[]; error: boolean }> {
  try {
    const res = await fetch("/api/v1/public/training-institutions?limit=300", { cache: "no-store" });
    if (!res.ok) return { rows: [], error: true };
    const json = (await res.json()) as TrainingInstitutionsPayload;
    return json.success && Array.isArray(json.data) ? { rows: json.data, error: false } : { rows: [], error: true };
  } catch {
    return { rows: [], error: true };
  }
}

export default function TrainingClient({ initialRows, initialError }: { initialRows: Institution[]; initialError: boolean }) {
  const [items, setItems] = useState<Institution[]>(initialRows);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [loaded, setLoaded] = useState(true);
  const [feedError, setFeedError] = useState(initialError);

  useEffect(() => {
    let active = true;
    if (!initialError) return;
    fetchTrainingInstitutions().then(({ rows, error }) => {
      if (!active) return;
      setItems(rows);
      setFeedError(error);
      setLoaded(true);
    });
    return () => {
      active = false;
    };
  }, [initialError]);

  const categories = useMemo(
    () => Array.from(new Set(items.map((item) => String(item.category || "").trim()).filter(Boolean))).sort(),
    [items]
  );

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return items.filter((item) => {
      const matchesTerm = !term || [item.name, item.location, item.category, item.description]
        .some((value) => String(value || "").toLowerCase().includes(term));
      const matchesCategory = !category || String(item.category || "") === category;
      return matchesTerm && matchesCategory;
    });
  }, [category, items, query]);

  return (
    <main className="min-h-screen bg-[#07111f] text-white">
      <header className="border-b border-cyan-300/15 bg-[#0b1628]">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-6 py-5">
          <Link href={"/public" as Route} className="flex items-center gap-3">
            <span className="relative h-12 w-16 overflow-hidden rounded-xl border border-cyan-300/20 bg-white/5">
              <Image src="/brand/cybo-one-mark.png" alt="" fill loading="lazy" sizes="64px" className="object-contain p-1.5" />
            </span>
            <span>
              <span className="block text-xl font-extrabold tracking-tight">Cybo One</span>
              <span className="block text-xs uppercase tracking-[0.28em] text-cyan-200/70">Training Network</span>
            </span>
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <Link href={"/public" as Route} className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2 text-sm font-bold text-cyan-100 hover:border-cyan-300/50">
              <ArrowLeft className="h-4 w-4" /> Public Home
            </Link>
            <Link href={"/public/ownerd?type=training_institution" as Route} className="rounded-xl bg-cyan-300 px-4 py-2 text-sm font-extrabold text-slate-950 hover:bg-cyan-200">
              Register Institution
            </Link>
          </div>
        </div>
      </header>

      <section className="border-b border-cyan-300/10 bg-[linear-gradient(135deg,#0b2a47_0%,#0d1d3d_55%,#08111f_100%)]">
        <div className="mx-auto grid max-w-7xl gap-8 px-6 py-14 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full border border-cyan-300/30 bg-cyan-300/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-cyan-100">
              <GraduationCap className="h-4 w-4" /> Training Institutions
            </p>
            <h1 className="mt-6 max-w-3xl text-4xl font-black leading-tight tracking-tight md:text-6xl">
              Hospitality schools using real operating workflows for student practice.
            </h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-cyan-50/80">
              Approved institutions can use Cybo One as a controlled training environment for front office, POS, HR, inventory, events, and hotel operations learning.
            </p>
          </div>
          <div className="rounded-3xl border border-cyan-300/20 bg-slate-950/50 p-6 shadow-2xl">
            <div className="grid gap-3 sm:grid-cols-3">
              <Metric label="Institutions" value={items.length} />
              <Metric label="Categories" value={categories.length} />
              <Metric label="Status" value={loaded ? "Current" : "..."} />
            </div>
            <p className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm leading-6 text-cyan-50/75">
              Approved training partners appear here after onboarding review.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-10">
        <div className="rounded-3xl border border-cyan-300/15 bg-[#0d1a2e] p-5">
          <div className="grid gap-3 md:grid-cols-[1fr_280px]">
            <label className="flex items-center gap-3 rounded-2xl border border-white/10 bg-slate-950/55 px-4">
              <Search className="h-5 w-5 text-cyan-200" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search institution, city, or program..."
                className="h-14 w-full bg-transparent text-sm text-white outline-none placeholder:text-cyan-100/40"
              />
            </label>
            <select
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              className="h-14 rounded-2xl border border-white/10 bg-slate-950/55 px-4 text-sm font-semibold text-white outline-none"
            >
              <option value="">All institution types</option>
              {categories.map((entry) => (
                <option key={entry} value={entry}>{entry}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-8 grid gap-5 lg:grid-cols-2">
          {filtered.map((item) => (
            <article key={item.id || item.name} className="rounded-3xl border border-cyan-300/15 bg-[#0d1a2e] p-6 shadow-[0_24px_70px_-50px_rgba(34,211,238,0.5)]">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-cyan-200/70">{item.category || "Training Institution"}</p>
                  <h2 className="mt-2 text-2xl font-black tracking-tight">{item.name || "Training Institution"}</h2>
                </div>
                <span className="rounded-full border border-emerald-300/30 bg-emerald-300/10 px-3 py-1 text-xs font-bold text-emerald-200">
                  Approved
                </span>
              </div>
              <div className="mt-4 flex flex-wrap gap-3 text-sm text-cyan-50/70">
                <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-cyan-200" /> {item.location || "Location pending"}</span>
                <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-cyan-200" /> Verified network partner</span>
              </div>
              <p className="mt-5 text-sm leading-7 text-cyan-50/75">{item.description || "Hospitality training partner on Cybo One."}</p>
            </article>
          ))}
        </div>

        {feedError ? (
          <div role="status" className="mt-8 rounded-3xl border border-amber-300/30 bg-amber-300/10 px-6 py-10 text-center">Training institution listings are temporarily unavailable. Please try again shortly.</div>
        ) : loaded && filtered.length === 0 ? (
          <div className="mt-8 rounded-3xl border border-dashed border-cyan-300/25 bg-[#0d1a2e] px-6 py-14 text-center">
            <UsersRound className="mx-auto h-10 w-10 text-cyan-200/70" />
            <h2 className="mt-4 text-2xl font-black">No approved training institutions yet</h2>
            <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-cyan-50/70">
              Registered schools will appear here after review. Training institution applications can be submitted from partner onboarding.
            </p>
            <Link href={"/public/ownerd?type=training_institution" as Route} className="mt-6 inline-flex rounded-xl bg-cyan-300 px-5 py-3 text-sm font-extrabold text-slate-950 hover:bg-cyan-200">
              Register Training Institution
            </Link>
          </div>
        ) : null}

        {!loaded ? (
          <div className="mt-8 rounded-3xl border border-cyan-300/15 bg-[#0d1a2e] px-6 py-10 text-center text-sm text-cyan-50/70">
            Loading approved training institutions...
          </div>
        ) : null}
      </section>
    </main>
  );
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-2xl border border-cyan-300/15 bg-slate-950/50 p-4">
      <p className="text-2xl font-black text-cyan-200">{typeof value === "number" ? value.toLocaleString() : value}</p>
      <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em] text-cyan-50/55">{label}</p>
    </div>
  );
}
